import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/alerts/api/config.ts";
import { getAlerts, type IGetAlertsInput } from "@/alerts/api/requests/machine-alerts.requests.ts";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { isAlertWithinRange } from "@/alerts/utils/alert-range.ts";
import type { Alert } from "@/alerts/types/machine-alert.ts";
import type { ApiResponse } from "@/shared/types/api.ts";

/**
 * All alerts for a single machine in the current date range (newest first,
 * ordered server-side). Drives the on-map alert markers and the alert-detail
 * card's prev/next.
 */
export const useMachineAlerts = (deviceMxid: string | undefined): Alert[] => {
  const { value } = useDateTimeRangeStore();

  const { data } = useQuery<ApiResponse<Alert[]>>({
    queryKey: [
      QUERY_KEYS.ALERTS,
      { device_mxid: deviceMxid, start_time: value.fromDate, end_time: value.toDate, ordering: "-timestamp" },
    ],
    queryFn: () =>
      getAlerts({
        device_mxid: deviceMxid!,
        start_time: value.fromDate,
        end_time: value.toDate,
        page: 1,
        itemsPerPage: 1000,
        // Server-side newest-first — the 1000-item page keeps the newest alerts.
        ordering: "-timestamp",
      } as IGetAlertsInput),
    enabled: !!deviceMxid,
  });

  // Defensive bound, same as `useLocationAlerts`: the request is windowed, but
  // nothing outside from→to may reach the UI (this feeds `/map`'s Images panel).
  return useMemo(
    () =>
      (data?.data ?? []).filter((alert) =>
        isAlertWithinRange(alert, value.fromDate, value.toDate),
      ),
    [data, value.fromDate, value.toDate],
  );
};
